import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Menu, Camera } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Navbar = () => {
  const { brand, isAuthenticated, setBrand } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error("Error during logout:", error);
        toast({
          title: "Logout failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setBrand(null);

      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });

      navigate("/");
    } catch (err) {
      console.error("Unexpected error during logout:", err);
      setBrand(null);
      toast({
        title: "Logout issue",
        description: "You were logged out but encountered an error.",
        variant: "destructive",
      });
      navigate("/");
    }
  };

  const NavItems = () => (
    <div className="flex flex-col gap-4 md:flex-row md:items-center">
      <Link to="/" className="text-sm font-medium transition-colors hover:text-primary">
        Home
      </Link>
      <Link to="/uploads" className="text-sm font-medium transition-colors hover:text-primary">
        Upload Demo
      </Link>

      {isAuthenticated ? (
        <>
          <Link to="/dashboard" className="text-sm font-medium transition-colors hover:text-primary">
            Dashboard
          </Link>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <span className="text-sm font-medium">{brand?.name}</span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col md:flex-row gap-4">
          <Link to="/login">
            <Button variant="outline" size="sm" className="w-full md:w-auto">
              Log in
            </Button>
          </Link>
          <Link to="/register">
            <Button size="sm" className="w-full md:w-auto">
              Sign up
            </Button>
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-sm">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <span className="text-xl font-bold flex items-center gap-1">
            <div className="relative inline-flex items-center">
              <svg width="0" height="0">
                <defs>
                  <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#2dd4bf" />
                    <stop offset="50%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#1d4ed8" />
                  </linearGradient>
                </defs>
              </svg>
              <Camera
                className="w-6 h-6"
                style={{
                  stroke: 'url(#logo-gradient)',
                  strokeWidth: 2.5
                }}
              />
            </div>
            SHOPSNAP
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-4">
          <NavItems />
        </nav>

        <Sheet>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent>
            <nav className="flex flex-col gap-4 mt-8">
              <NavItems />
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Navbar;
